/**
 * SmartRuleBuilder
 * Modal for creating an ignore rule pre-filled from an unmatched transaction.
 * Suggestion priority: counterparty > merchantName > description
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import type { UnmatchedTransaction } from '../../api/external-payments-hub.api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RuleField = 'description' | 'counterparty' | 'merchantName' | 'paymentChannel';
type RuleOperator = 'contains' | 'starts_with' | 'exact' | 'equals';

interface RuleDraft {
  field: RuleField;
  operator: RuleOperator;
  value: string;
}

export interface SmartRuleBuilderProps {
  transaction: UnmatchedTransaction;
  onSubmit: (rule: { field: string; operator: string; value: string }) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FIELD_LABELS: Record<RuleField, string> = {
  description: 'Description',
  counterparty: 'Counterparty',
  merchantName: 'Merchant Name',
  paymentChannel: 'Payment Channel',
};

const OPERATOR_LABELS: Record<RuleOperator, string> = {
  contains: 'contains',
  starts_with: 'starts with',
  exact: 'exactly matches',
  equals: 'equals',
};

function suggestRule(tx: UnmatchedTransaction): RuleDraft {
  const firstCounterparty = tx.counterparties?.[0];
  if (firstCounterparty?.name) {
    return { field: 'counterparty', operator: 'contains', value: firstCounterparty.name };
  }
  if (tx.merchantName) {
    return { field: 'merchantName', operator: 'exact', value: tx.merchantName };
  }
  return { field: 'description', operator: 'contains', value: tx.description };
}

/** Returns only the fields that have data on the transaction */
function availableFields(tx: UnmatchedTransaction): RuleField[] {
  const fields: RuleField[] = ['description'];
  const firstCounterparty = tx.counterparties?.[0];
  if (firstCounterparty?.name) {
    fields.push('counterparty');
  }
  if (tx.merchantName) {
    fields.push('merchantName');
  }
  if (tx.paymentChannel) {
    fields.push('paymentChannel');
  }
  return fields;
}

const DEFAULT_OPERATORS_FOR_FIELD: Record<RuleField, RuleOperator[]> = {
  description: ['contains', 'starts_with', 'exact'],
  counterparty: ['contains', 'exact'],
  merchantName: ['exact', 'contains'],
  paymentChannel: ['equals'],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SmartRuleBuilder({
  transaction,
  onSubmit,
  onCancel,
  isSubmitting,
  submitError,
}: SmartRuleBuilderProps) {
  const suggestion = suggestRule(transaction);
  const fields = availableFields(transaction);

  const [draft, setDraft] = useState<RuleDraft>(suggestion);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap: capture Tab key within the dialog
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    const focusable = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    first?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const handleFieldChange = (field: RuleField) => {
    const ops = DEFAULT_OPERATORS_FOR_FIELD[field];
    const operator: RuleOperator = ops[0] ?? 'contains';
    let value = draft.value;

    // Reset value to sensible default for new field
    if (field === 'counterparty') {
      const firstCp = transaction.counterparties?.[0];
      value = firstCp?.name ?? draft.value;
    } else if (field === 'merchantName') {
      value = transaction.merchantName ?? draft.value;
    } else if (field === 'paymentChannel') {
      value = transaction.paymentChannel ?? draft.value;
    } else if (field === 'description') {
      value = transaction.description;
    }

    setDraft({ field, operator, value });
  };

  const handleSubmit = async () => {
    await onSubmit(draft);
  };

  const previewText = `Will match transactions where ${FIELD_LABELS[draft.field]} ${
    OPERATOR_LABELS[draft.operator]
  } "${draft.value}"`;

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="smart-rule-builder-title"
        className="relative w-full max-w-md rounded-xl border border-border bg-background shadow-xl p-6 mx-4"
        data-testid="smart-rule-builder"
      >
        <h2
          id="smart-rule-builder-title"
          className="text-base font-semibold text-foreground mb-1"
        >
          Ignore all transactions like this
        </h2>
        <p className="text-sm text-muted-foreground mb-5">
          Create a rule to permanently hide similar transactions from this feed.
        </p>

        {/* Form rows: selectors on top, value input full-width below */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex gap-2">
            <select
              className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-base md:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              value={draft.field}
              onChange={(e) => handleFieldChange(e.target.value as RuleField)}
              data-testid="rule-field-select"
              aria-label="Rule field"
            >
              {fields.map((f) => (
                <option key={f} value={f}>
                  {FIELD_LABELS[f]}
                </option>
              ))}
            </select>

            <select
              className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-base md:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              value={draft.operator}
              onChange={(e) => setDraft((d) => ({ ...d, operator: e.target.value as RuleOperator }))}
              data-testid="rule-operator-select"
              aria-label="Rule operator"
            >
              {DEFAULT_OPERATORS_FOR_FIELD[draft.field].map((op) => (
                <option key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </option>
              ))}
            </select>
          </div>

          <Input
            type="text"
            className="rounded-md border-input bg-background px-2 py-1.5 text-foreground"
            value={draft.value}
            onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
            data-testid="rule-value-input"
            aria-label="Rule value"
            placeholder="Value to match"
          />
        </div>

        {/* Live preview */}
        <p
          className="text-xs text-muted-foreground bg-muted/30 rounded-md px-3 py-2 mb-4 italic"
          data-testid="rule-preview-text"
        >
          {previewText}
        </p>

        {/* Error */}
        {submitError && (
          <p className="text-sm text-destructive mb-3" role="alert">
            {submitError}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="rule-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitting || !draft.value.trim()}
            data-testid="rule-submit-btn"
          >
            {isSubmitting ? 'Creating...' : 'Create Rule'}
          </Button>
        </div>
      </div>
    </div>
  );
}
